import { MetadataKey, type TrialType, type User } from '@revelio/data-access';

import type { ImpersonationState, UserOverrides } from './impersonation.model';
import {
  getImpersonationStateSnapshot,
  useImpersonationStoreBase,
} from './impersonation.store';

const DEFAULT_TRIAL_EXPIRATION_DAYS = 30;

const getTrialExpirationDate = (days: number) => {
  const expirationDate = new Date();
  expirationDate.setDate(expirationDate.getDate() + days);
  return expirationDate.toISOString();
};

const normalizeOverrides = (overrides: UserOverrides): UserOverrides => {
  const normalizedOverrides = { ...overrides };

  if (overrides.isTrialUser !== undefined) {
    normalizedOverrides.live = !overrides.isTrialUser;
  }

  if (overrides.isTrialUser && !overrides.expirationDate) {
    normalizedOverrides.expirationDate = getTrialExpirationDate(
      DEFAULT_TRIAL_EXPIRATION_DAYS
    );
  }

  return normalizedOverrides;
};

const getMetadataValue = (
  metadata: User['metadata'],
  key: MetadataKey
): string | undefined =>
  metadata?.find((item) => item?.key === key)?.value ?? undefined;

export type ImpersonationStoreState = ImpersonationState;

export const useImpersonationSelector = <SelectedState>(
  selector: (state: ImpersonationStoreState) => SelectedState
) => useImpersonationStoreBase((store) => selector(store.state));

export const selectImpersonationState = (state: ImpersonationStoreState) =>
  state;
export const selectIsImpersonating = (state: ImpersonationStoreState) =>
  state.isImpersonating;
export const selectImpersonatedUser = (state: ImpersonationStoreState) =>
  state.impersonatedUser;
export const selectImpersonatedUserId = (state: ImpersonationStoreState) =>
  state.impersonatedUserId;
export const selectImpersonationOverrides = (state: ImpersonationStoreState) =>
  state.overrides;
export const selectImpersonationActive = (state: ImpersonationStoreState) =>
  state.isImpersonating || state.overrides !== null;

export const useImpersonationStore = () => {
  return useImpersonationSelector(selectImpersonationState);
};

export function startImpersonation(user: Partial<User>) {
  useImpersonationStoreBase.getState().actions.startImpersonation(user);
}

export function setOverrides(overrides: UserOverrides) {
  const normalizedOverrides = normalizeOverrides(overrides);
  useImpersonationStoreBase
    .getState()
    .actions.setOverrides(normalizedOverrides);
}

export function clearImpersonation() {
  useImpersonationStoreBase.getState().actions.clearImpersonation();
}

export function getEffectiveOverrides(): UserOverrides | null {
  const state = getImpersonationStateSnapshot();

  if (state.isImpersonating && state.impersonatedUser) {
    const impersonatedUser = state.impersonatedUser;
    const overrides: UserOverrides = {};
    const hasLiveFlag =
      impersonatedUser.live !== undefined && impersonatedUser.live !== null;

    if (hasLiveFlag) {
      overrides.live = impersonatedUser.live ?? undefined;
      overrides.isTrialUser = !impersonatedUser.live;
    }

    if (impersonatedUser.client_name !== undefined) {
      overrides.clientName = impersonatedUser.client_name ?? undefined;
    }

    if (impersonatedUser.role !== undefined && impersonatedUser.role !== null) {
      overrides.role = impersonatedUser.role;
    }

    if (impersonatedUser.tabs !== undefined && impersonatedUser.tabs !== null) {
      overrides.tabs = impersonatedUser.tabs;
    }

    if (
      impersonatedUser.linkup_postings !== undefined &&
      impersonatedUser.linkup_postings !== null
    ) {
      overrides.linkupPostings = impersonatedUser.linkup_postings;
    }

    const trialTypeValue = getMetadataValue(
      impersonatedUser.metadata,
      MetadataKey.TrialType
    );
    if (trialTypeValue) {
      overrides.trialType = trialTypeValue as TrialType;
    }

    const expirationDateValue = getMetadataValue(
      impersonatedUser.metadata,
      MetadataKey.ExpirationDate
    );
    if (expirationDateValue) {
      overrides.expirationDate = expirationDateValue;
    }

    const tourCompletedValue = getMetadataValue(
      impersonatedUser.metadata,
      MetadataKey.TourCompleted
    );
    if (tourCompletedValue !== undefined) {
      overrides.tourCompleted = tourCompletedValue === 'true';
    }

    const stepsCompletedValue = getMetadataValue(
      impersonatedUser.metadata,
      MetadataKey.StepsCompleted
    );
    if (stepsCompletedValue !== undefined) {
      overrides.stepsCompleted = stepsCompletedValue;
    }

    return overrides;
  }

  return state.overrides;
}
